import { useState } from 'react';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/HistoryEduOutlined';
import { useNavigate } from 'react-router-dom';
import { listFisExamStudents, saveFisExamRegistration } from '../../api/exams';
import { useAuth } from '../../context/authContext';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import {
  FIS_EXAM_STATUSES,
  FisExamRegistrationInput,
  FisExamStatus,
  FisExamStudent,
  FisFeeFilter,
  FisRegistrationFilter,
} from '../../types/exam';
import { EmptyRows, ExamListFilters, FeeStatusChip } from './examListShared';
import {
  EMPTY_EXAM_FILTERS,
  ExamListFilterValues,
  extractErrorMessage,
  FIS_STATUS_LABEL,
  formatMoney,
} from './examListUtils';

const toDateInput = (value: string | null) => (value ? value.slice(0, 10) : '');

/** Record or update one student's FIS exam registration. One registration
 * per student: saving again updates it. The FIS Exam Fee is paid through
 * the student's Payments, never here. */
const RegistrationDialog = ({ row, onClose }: { row: FisExamStudent; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<FisExamStatus>(row.registration?.status ?? 'REGISTERED');
  const [examDate, setExamDate] = useState(toDateInput(row.registration?.examDate ?? null));
  const [notes, setNotes] = useState(row.registration?.notes ?? '');

  const mutation = useMutation({
    mutationFn: (payload: FisExamRegistrationInput) =>
      saveFisExamRegistration(row.studentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fis-exam'] });
      queryClient.invalidateQueries({ queryKey: ['student-timeline', row.studentId] });
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {row.registration ? 'Update FIS exam registration' : 'Record FIS exam registration'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {row.student.firstName} {row.student.lastName} ({row.student.studentNumber}) ·{' '}
            {row.course?.name ?? '—'}
          </Typography>
          {row.fisExamFee && row.fisExamFee.status !== 'PAID' && (
            <Alert severity="warning">
              FIS Exam Fee not fully paid:{' '}
              {formatMoney(row.fisExamFee.outstandingAmount, row.fisExamFee.currency)} outstanding.
              Record and confirm the payment from the student's Payments.
            </Alert>
          )}
          {mutation.isError && (
            <Alert severity="error">{extractErrorMessage(mutation.error)}</Alert>
          )}
          <TextField
            select
            label="Status"
            fullWidth
            value={status}
            onChange={(e) => setStatus(e.target.value as FisExamStatus)}
          >
            {FIS_EXAM_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {FIS_STATUS_LABEL[s]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Exam date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={examDate}
            onChange={(e) => setExamDate(e.target.value)}
          />
          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 2000 }}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={mutation.isPending}
          onClick={() =>
            mutation.mutate({
              status,
              examDate: examDate ? new Date(`${examDate}T00:00:00.000Z`).toISOString() : null,
              notes: notes.trim() || null,
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** CRM → FIS Exam: students on a Foundation Exam programme (Master Data →
 * Programs → "Foundation Exam programme"), their FIS Exam Fee and their
 * exam registration. Only these students are ever listed or charged the fee. */
export const FisExamPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('academic', 'update');
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<ExamListFilterValues>(EMPTY_EXAM_FILTERS);
  const [registration, setRegistration] = useState<FisRegistrationFilter>('ALL');
  const [fee, setFee] = useState<FisFeeFilter>('ALL');
  const [editing, setEditing] = useState<FisExamStudent | null>(null);

  const listQuery = useQuery({
    queryKey: [
      'fis-exam',
      paginationModel.page,
      paginationModel.pageSize,
      filters,
      registration,
      fee,
      selectedBranchId,
    ],
    queryFn: () =>
      listFisExamStudents({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: filters.search || undefined,
        universityId: filters.universityId || undefined,
        intakeId: filters.intakeId || undefined,
        counselorId: filters.counselorId || undefined,
        branchId: selectedBranchId,
        registration,
        fee,
      }),
    placeholderData: (prev) => prev,
  });

  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  const columns: GridColDef<FisExamStudent>[] = [
    {
      field: 'studentNumber',
      headerName: 'Student #',
      width: 120,
      valueGetter: (_v, row) => row.student.studentNumber,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => `${row.student.firstName} ${row.student.lastName}`,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      valueGetter: (_v, row) => row.student.phone,
    },
    {
      field: 'branch',
      headerName: 'Branch',
      width: 120,
      valueGetter: (_v, row) => row.branch?.name ?? '—',
    },
    {
      field: 'university',
      headerName: 'University',
      width: 170,
      valueGetter: (_v, row) => row.university?.name ?? '—',
    },
    {
      field: 'course',
      headerName: 'Programme',
      width: 170,
      valueGetter: (_v, row) => row.course?.name ?? '—',
    },
    {
      field: 'intake',
      headerName: 'Intake',
      width: 120,
      valueGetter: (_v, row) => row.intake?.name ?? '—',
    },
    {
      field: 'counselor',
      headerName: 'Counselor',
      width: 150,
      valueGetter: (_v, row) => `${row.counselor.firstName} ${row.counselor.lastName}`,
    },
    {
      field: 'fisFeeAmount',
      headerName: 'FIS Exam Fee',
      width: 130,
      valueGetter: (_v, row) =>
        row.fisExamFee ? formatMoney(row.fisExamFee.requiredAmount, row.fisExamFee.currency) : '—',
    },
    {
      field: 'fisFeeStatus',
      headerName: 'Fee status',
      width: 170,
      sortable: false,
      renderCell: (params) =>
        params.row.fisExamFee ? <FeeStatusChip fee={params.row.fisExamFee} /> : '—',
    },
    {
      field: 'registration',
      headerName: 'Exam registration',
      width: 160,
      sortable: false,
      renderCell: (params) =>
        params.row.registration ? (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={FIS_STATUS_LABEL[params.row.registration.status]}
          />
        ) : (
          <Chip size="small" variant="outlined" label="Not registered" />
        ),
    },
    {
      field: 'examDate',
      headerName: 'Exam date',
      width: 110,
      valueGetter: (_v, row) =>
        row.registration?.examDate ? new Date(row.registration.examDate).toLocaleDateString() : '—',
    },
    ...(canManage
      ? [
          {
            field: 'actions',
            headerName: '',
            width: 110,
            sortable: false,
            renderCell: (params) => (
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(params.row);
                }}
              >
                {params.row.registration ? 'Update' : 'Register'}
              </Button>
            ),
          } satisfies GridColDef<FisExamStudent>,
        ]
      : []),
  ];

  const hasFilters =
    !!filters.search ||
    !!filters.universityId ||
    !!filters.intakeId ||
    !!filters.counselorId ||
    registration !== 'ALL' ||
    fee !== 'ALL';

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={SchoolIcon}
        title="FIS Exam"
        subtitle="Students registered for a Foundation Exam programme, with their FIS Exam Fee and exam registration."
      />

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Only students on a Foundation Exam programme (Master Data → Programs) are listed and charged
        the FIS Exam Fee. The fee is paid and confirmed through the student's Payments; an uploaded
        slip awaiting confirmation is not paid.
      </Alert>

      <ExamListFilters
        values={filters}
        onChange={(values) => {
          setFilters(values);
          resetPage();
        }}
      >
        <TextField
          select
          size="small"
          label="Exam registration"
          sx={{ minWidth: 170 }}
          value={registration}
          onChange={(e) => {
            setRegistration(e.target.value as FisRegistrationFilter);
            resetPage();
          }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="NOT_REGISTERED">Not registered</MenuItem>
          {FIS_EXAM_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {FIS_STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="FIS fee"
          sx={{ minWidth: 140 }}
          value={fee}
          onChange={(e) => {
            setFee(e.target.value as FisFeeFilter);
            resetPage();
          }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="PAID">Paid</MenuItem>
          <MenuItem value="NOT_PAID">Not fully paid</MenuItem>
        </TextField>
      </ExamListFilters>

      {listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load FIS Exam students: {extractErrorMessage(listQuery.error)}
        </Alert>
      ) : (
        <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{ toolbar: TableToolbar, noRowsOverlay: EmptyRows }}
            slotProps={{
              noRowsOverlay: {
                message: hasFilters
                  ? 'No Foundation Exam students match these filters.'
                  : 'No students are on a Foundation Exam programme yet.',
              },
            }}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/students/${params.row.studentId}`)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}

      {editing && <RegistrationDialog row={editing} onClose={() => setEditing(null)} />}
    </Box>
  );
};
